import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { DatePipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { RunnerPairingService, type ProjectRunner, type ProjectRunners } from '../../services/runner-pairing.service';
import { saveBlob } from '../../shared/file-download';
import {
  cleanCode,
  digitsOf,
  isSixDigits,
  packageFileName,
  pairingErrorKey,
  waitForNewRunner,
} from '../runner-connect-help/runner-pairing';

export { NACHLADEN_ABSTAND_MS, NACHLADEN_VERSUCHE } from '../runner-connect-help/runner-pairing';

const HINWEISE = ['nutzen', 'verwalten', 'alle'] as const;

type Ladezustand = 'loading' | 'loaded' | 'failed';

@Component({
  selector: 'app-projekt-rechner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, MatButtonModule, MatProgressSpinner, TranslatePipe],
  templateUrl: './projekt-rechner.component.html',
  styleUrl: './projekt-rechner.component.scss',
})
export class ProjektRechnerComponent implements OnInit {
  private readonly service = inject(RunnerPairingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();

  readonly hinweise = HINWEISE;

  readonly listState = signal<Ladezustand>('loading');
  readonly runners = signal<ProjectRunner[]>([]);
  readonly canManage = signal(false);

  readonly packages = signal<string[]>([]);
  readonly packagesState = signal<Ladezustand>('loading');
  readonly downloading = signal<string | null>(null);

  readonly code = signal('');
  readonly busy = signal(false);
  readonly connectError = signal<string | null>(null);
  readonly connectedRunner = signal<string | null>(null);
  readonly waitingForRunner = signal(false);
  readonly runnerNotReported = signal(false);
  readonly refreshFailed = signal(false);
  private waitSubscription?: Subscription;
  private reloadSubscription?: Subscription;

  readonly detaching = signal<string | null>(null);
  readonly detachFailed = signal(false);

  readonly canSubmit = computed(() => isSixDigits(this.code()) && !this.busy());

  ngOnInit(): void {
    this.reload();
    this.service
      .listPackages()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (platforms) => {
          this.packages.set(platforms);
          this.packagesState.set('loaded');
        },
        error: () => this.packagesState.set('failed'),
      });
  }

  reload(): void {
    this.stopWaiting();
    this.reloadSubscription?.unsubscribe();
    this.listState.set('loading');
    this.reloadSubscription = this.service
      .listProjectRunners(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (liste) => {
          this.runners.set(liste.runners);
          this.canManage.set(liste.canManage);
          this.listState.set('loaded');
        },
        error: () => this.listState.set('failed'),
      });
  }

  onCodeInput(field: HTMLInputElement): void {
    const cleaned = cleanCode(field.value);
    field.value = cleaned;
    this.code.set(cleaned);
  }

  submit(): void {
    if (!this.canSubmit()) {
      return;
    }
    this.busy.set(true);
    this.connectError.set(null);
    this.connectedRunner.set(null);
    this.stopWaiting();
    const before = this.runners();
    this.service
      .confirm(digitsOf(this.code()), this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (confirmation) => {
          this.connectedRunner.set(confirmation.runnerName);
          this.code.set('');
          this.busy.set(false);
          this.waitFor(before);
        },
        error: (failure: HttpErrorResponse) => {
          this.connectError.set(pairingErrorKey(failure.status));
          this.busy.set(false);
        },
      });
  }

  download(platform: string): void {
    this.downloading.set(platform);
    this.service
      .downloadPackage(platform)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (blob) => {
          saveBlob(blob, packageFileName(platform));
          this.downloading.set(null);
        },
        error: () => {
          this.connectError.set('runnerPairing.error.downloadFailed');
          this.downloading.set(null);
        },
      });
  }

  detach(runner: ProjectRunner): void {
    this.detaching.set(runner.id);
    this.detachFailed.set(false);
    this.service
      .detachFromProject(this.projectId(), runner.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.runners.update((liste) => liste.filter((r) => r.id !== runner.id));
          this.detaching.set(null);
        },
        error: (failure: HttpErrorResponse) => {
          this.detaching.set(null);
          if (failure.status === 404) {
            this.reload();
            return;
          }
          this.detachFailed.set(true);
        },
      });
  }

  private waitFor(before: ProjectRunner[]): void {
    this.reloadSubscription?.unsubscribe();
    this.waitingForRunner.set(true);
    const arrived = (liste: ProjectRunners): boolean => liste.runners.some((r) => isNewInProject(r, before));
    let found = false;
    this.waitSubscription = waitForNewRunner(() => this.service.listProjectRunners(this.projectId()), arrived)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (liste) => {
          this.runners.set(liste.runners);
          this.canManage.set(liste.canManage);
          this.listState.set('loaded');
          found = arrived(liste);
        },
        error: () => {
          this.waitingForRunner.set(false);
          this.refreshFailed.set(true);
        },
        complete: () => {
          this.waitingForRunner.set(false);
          this.runnerNotReported.set(!found);
        },
      });
  }

  private stopWaiting(): void {
    this.waitSubscription?.unsubscribe();
    this.waitingForRunner.set(false);
    this.runnerNotReported.set(false);
    this.refreshFailed.set(false);
  }
}

/** Gleichnamig reicht nicht: Der Server legt bei belegtem Namen "Name (2)" an oder übernimmt einen getrennten Rechner. */
function isNewInProject(runner: ProjectRunner, before: ProjectRunner[]): boolean {
  const known = before.find((r) => r.id === runner.id);
  if (!known) {
    return true;
  }
  return !known.connected && runner.connected;
}
